import {
  ComboButton,
  Divider,
  StyledActionMenu,
} from 'modules/experiments/components/pages/edit/sections/content/Styled';
import { TooltipWrapper } from 'modules/experiments/components/ui';
import {
  STATES_WITH_DELETE_ACTION,
  STATES_WITH_LAUNCH_ACTION,
  dataTestId,
} from 'modules/experiments/constants';
import { useModalContext, useTakeoverExperimentContext } from 'modules/experiments/contexts';
import { useVariantTemplateValidationContext } from 'modules/experiments/contexts/VariantTemplateValidationContext';
import {
  getTakeoverCampaignConversions,
  getTakeoverCampaignMessageMedium,
  getTakeoverCampaignType,
  getTakeoverExperimentId,
  getTakeoverExperimentName,
  getTakeoverExperimentState,
} from 'modules/experiments/ducks/selectors';
import {
  useDeleteExperiment,
  useExperimentsTranslations,
  useExperimentsUserPermission,
  useLaunchExperiment,
} from 'modules/experiments/hooks';
import { usePrivateExperimentTakeover } from 'modules/experiments/takeover/hooks';
import {
  ExperimentNavigateOptions,
  SaveExperimentSettingsResponse,
} from 'modules/experiments/types';
import {
  generateUpdateExperimentSettingArgs,
  getExperimentFormSettingDefaultValue,
  isTriggerCampaignType,
} from 'modules/experiments/utils';
import { addNegativeToast } from 'modules/toastContainer';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import {
  ActionMenuOption,
  AllowedActionMenuElements,
  MENU_ALIGNMENTS,
  MENU_DIRECTIONS,
} from 'ui/actionMenu/types';
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  Button,
  PrimaryButton,
  SecondaryButton,
  SecondaryDestructiveButton,
} from 'ui/button';
import { WarningFill } from 'ui/icons';
import { ChevronUp } from 'ui/menu/Styled';
import { MODAL_VARIANTS } from 'ui/modal';
import { colors } from 'ui/theme';
import { getExperimentMonitorLink } from 'utils/appRoutes';
import { StyledFooter, UnsavedChangesMessage } from './Styled';

export const ExperimentTakeoverFooter = ({ onClose }: { onClose: () => void }) => {
  const { t } = useExperimentsTranslations();
  const campaignMessageMedium = useSelector(getTakeoverCampaignMessageMedium);
  const { nonManageAndLaunchPermission, viewOnlyPermission } =
    useExperimentsUserPermission(campaignMessageMedium);
  const { experimentHasErrors } = useVariantTemplateValidationContext();
  const { closeExperimentTakeover } = usePrivateExperimentTakeover();
  const dispatch = useDispatch();
  const {
    unsavedChanges: { variantList: unsavedVariant, settings: unsavedExperimentSettings },
    formActions: { saveDirtyExperimentSettings, asyncDispatchSaveDirtyVariantCard },
    sendTimeMetaInfo: { projectTimeZone, controlStartTime },
    isBlastCampaign,
    isTriggerCampaign,
    isExperimentSaving,
    isSendTimeExperiment,
    saveExperiment,
  } = useTakeoverExperimentContext();
  const { showModal, hideModal } = useModalContext();
  const navigate = useNavigate();
  const {
    formState: { isDirty, isValid },
    handleSubmit,
    getValues,
    reset,
  } = useFormContext();

  const experimentId = useSelector(getTakeoverExperimentId);
  const experimentName = useSelector(getTakeoverExperimentName);
  const experimentState = useSelector(getTakeoverExperimentState);
  const campaignType = useSelector(getTakeoverCampaignType);
  const campaignConversions = useSelector(getTakeoverCampaignConversions);
  const deleteExperiment = useDeleteExperiment({
    id: experimentId,
    name: experimentName,
    onComplete() {
      hideModal();
      onClose();
      closeExperimentTakeover();
    },
  });

  const launchExperiment = useLaunchExperiment({
    id: experimentId,
    onError() {
      hideModal();
    },
    onSuccess() {
      hideModal();
      closeExperimentTakeover();
      navigate(getExperimentMonitorLink(experimentId), {
        state: { experimentName },
      } as ExperimentNavigateOptions);
    },
  });

  const isLaunchableExperiment =
    isTriggerCampaignType(campaignType) && STATES_WITH_LAUNCH_ACTION.includes(experimentState);
  const launchButtonTooltip = (() => {
    switch (true) {
      case nonManageAndLaunchPermission:
        return t('permissions.launchExperiment');
      case experimentHasErrors:
        return t('takeover.footer.tooltip.launchButton');
      case unsavedExperimentSettings || unsavedVariant:
        return t('takeover.footer.tooltip.unsavedChanges');

      default:
        return null;
    }
  })();

  const onSubmitSuccess = async (): Promise<SaveExperimentSettingsResponse> => {
    // Save any unsaved variant card first
    if (unsavedVariant) {
      await asyncDispatchSaveDirtyVariantCard();
    }

    const updateExperimentFormValues = getValues();
    const updateExperimentSettingsArgs = generateUpdateExperimentSettingArgs({
      experimentId,
      formValues: updateExperimentFormValues,
      campaignConversions,
      controlStartTime,
      isBlastCampaign,
      isTriggerCampaign,
      isSendTimeExperiment,
    });
    const response = await saveExperiment(updateExperimentSettingsArgs);

    // If save is successful then reset the setting form state
    if (response.isSuccess) {
      const newDefaultFormState = getExperimentFormSettingDefaultValue(
        response.data,
        projectTimeZone,
      );
      reset(newDefaultFormState);
    }
    return response;
  };

  const onSubmitSuccessWithClose = async () => {
    const response = await onSubmitSuccess();
    if (response.isSuccess) {
      onClose();
      closeExperimentTakeover();
    }
  };

  const onSubmitFailure = () => {
    dispatch(addNegativeToast(t('settings.errors.validation')));
  };

  // We want to save any dirty forms whenever
  // dispatchSaveDirtyExperimentSettings is called
  // And, call the corresponding onSuccess and onError callbacks
  useEffect(() => {
    const saveChanges = async () => {
      if (isDirty && isValid) {
        const response = await onSubmitSuccess();
        if (response.isSuccess) saveDirtyExperimentSettings.onSuccess();
        if (!response.isSuccess) saveDirtyExperimentSettings.onError();
      } else if (isDirty && !isValid) {
        saveDirtyExperimentSettings.onError();
        onSubmitFailure();
      }
    };
    saveChanges();
  }, [saveDirtyExperimentSettings.trigger]);

  const saveAndExitMenuOption: ActionMenuOption[] = [
    {
      as: AllowedActionMenuElements.Button,
      content: <>{t('buttons.saveAndExit')}</>,
      id: 'test-save-and-exit',
      onClick: handleSubmit(onSubmitSuccessWithClose, onSubmitFailure),
    },
  ];

  return (
    <StyledFooter id="takeover-styled-footer">
      {unsavedExperimentSettings && (
        <UnsavedChangesMessage data-test={dataTestId.takeoverUnsavedChanges}>
          <WarningFill color={colors.textSecondary} />
          {t('announcement.unsavedChange')}
        </UnsavedChangesMessage>
      )}

      {STATES_WITH_DELETE_ACTION.includes(experimentState) && (
        <TooltipWrapper
          tooltipContent={nonManageAndLaunchPermission ? t('permissions.deleteExperiment') : null}
        >
          <SecondaryDestructiveButton
            data-test={dataTestId.takeoverDeleteButton}
            isDisabled={nonManageAndLaunchPermission}
            onClick={() =>
              showModal({
                variant: MODAL_VARIANTS.DELETE,
                id: 'delete-experiment',
                title: t('takeover.footer.modal.deleteExperiment.header'),
                body: (
                  <Trans
                    i18nKey="experiments:takeover.footer.modal.deleteExperiment.body"
                    values={{ experimentName }}
                    components={{ bold: <strong /> }}
                  />
                ),
                buttonText: t('takeover.footer.modal.deleteExperiment.button'),
                inProgressButtonText: t('takeover.footer.modal.deleteExperiment.progress'),
                onConfirm: () => {
                  deleteExperiment();
                },
              })
            }
          >
            {t('buttons.deleteExperiment')}
          </SecondaryDestructiveButton>
        </TooltipWrapper>
      )}
      <TooltipWrapper
        tooltipContent={nonManageAndLaunchPermission ? t('permissions.saveExperiment') : null}
        /**
         * We want to show a simple button if experiment is not launchable,
         * and a combo button if it is launchable
         * to not exceed three action buttons in the footer
         */
      >
        {isLaunchableExperiment ? (
          <ComboButton id="save-exit-button-group">
            <SecondaryButton
              data-test={dataTestId.takeoverSaveButton}
              isLoading={isExperimentSaving}
              isDisabled={isExperimentSaving || viewOnlyPermission}
              onClick={handleSubmit(onSubmitSuccess, onSubmitFailure)}
            >
              {t('buttons.save')}
            </SecondaryButton>
            <Divider />
            <StyledActionMenu
              data-test="save-exit-action-menu"
              id="save-exit-action-menu"
              align={MENU_ALIGNMENTS.RIGHT}
              options={saveAndExitMenuOption}
              direction={MENU_DIRECTIONS.TOP}
              automaticallyAvoidCollisions={true}
            >
              <Button size={BUTTON_SIZES.MEDIUM} variant={BUTTON_VARIANTS.SECONDARY}>
                <ChevronUp data-test="save-exit-chevron" height={16} width={16} />
              </Button>
            </StyledActionMenu>
          </ComboButton>
        ) : (
          <SecondaryButton
            data-test={dataTestId.takeoverSaveExitButtonSecondary}
            isLoading={isExperimentSaving}
            isDisabled={isExperimentSaving || viewOnlyPermission}
            onClick={handleSubmit(onSubmitSuccessWithClose, onSubmitFailure)}
          >
            {t('buttons.saveAndExit')}
          </SecondaryButton>
        )}
      </TooltipWrapper>
      {!isLaunchableExperiment ? (
        <TooltipWrapper
          tooltipContent={nonManageAndLaunchPermission ? t('permissions.saveExperiment') : null}
          /**
           * Only display the save button if not launchable (blast)
           */
        >
          <PrimaryButton
            data-test={dataTestId.takeoverSaveButton}
            isLoading={isExperimentSaving}
            isDisabled={isExperimentSaving || viewOnlyPermission}
            onClick={handleSubmit(onSubmitSuccess, onSubmitFailure)}
          >
            {t('buttons.save')}
          </PrimaryButton>
        </TooltipWrapper>
      ) : null}
      {isLaunchableExperiment && (
        <TooltipWrapper tooltipContent={launchButtonTooltip}>
          <PrimaryButton
            data-test={dataTestId.takeoverLaunchButton}
            isDisabled={
              nonManageAndLaunchPermission ||
              experimentHasErrors ||
              unsavedExperimentSettings ||
              unsavedVariant
            }
            onClick={() =>
              showModal({
                variant: MODAL_VARIANTS.WARNING,
                id: 'launch-experiment',
                title: t('takeover.footer.modal.launchExperiment.header'),
                body: t('takeover.footer.modal.launchExperiment.body'),
                buttonText: t('takeover.footer.modal.launchExperiment.button'),
                inProgressButtonText: t('takeover.footer.modal.launchExperiment.progress'),
                isDestructive: false,
                onConfirm: () => {
                  launchExperiment();
                },
              })
            }
          >
            {t('button.launchExperiment')}
          </PrimaryButton>
        </TooltipWrapper>
      )}
    </StyledFooter>
  );
};
